import { ComponentRef, Injectable, OnDestroy } from '@angular/core';
import { AppService } from '@app/app.service';
import {
  MarineInsuranceSidebarBeneficiaryFormComponent,
  MarineInsuranceSidebarDescriptionManagementFormComponent,
  MarineInsuranceSidebarFromToFormComponent,
  MarineInsuranceSidebarQuoteFormComponent,
  MarineInsuranceSidebarQuoteManagementFormComponent,
  MarineInsuranceSidebarShipmentDetailsFormComponent,
} from '@shared/marine-insurance-shared/components';
import { ShipmentModel, ShortModel } from '@data-layer/models/common';
import { MarineInsuranceFacade } from './marine-insurance.facade';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { MarineInsuranceQuoteFormModel, MarineInsuranceQuoteManagementFormModel } from '@shared/marine-insurance-shared/models';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { enumMarineInsuranceEntityType } from '@data-layer/enums/marine-insurance';
import { IsfModel } from '@data-layer/models/isf';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { LCLShipmentModel, QuoteModel } from '@data-layer/models/lcl';
import {
  MarineInsuranceInsuredValueModel,
  MarineInsuranceManagementModel,
  MarineInsuranceModel,
} from '@data-layer/models/marine-insurance';
import { MarineInsuranceState } from '@shared/marine-insurance-shared/services/marine-insurance.state';
import { PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { CompanyModel } from '@data-layer/models/companies';
import { CompaniesService } from '@services/companies.service';
import { EntityModel } from '@data-layer/models/entities';
import { enumPaymentProvider } from '@data-layer/enums/payments';
import { AlertService } from '@services/alert.service';

@Injectable({ providedIn: 'root' })
export class MarineInsuranceSideformService implements OnDestroy {
  public isSidebarClosing$: ReplaySubject<boolean> = new ReplaySubject();
  public isModelUpdatedInSidebar$: ReplaySubject<boolean> = new ReplaySubject();

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private appService: AppService,
    private marineInsuranceFacade: MarineInsuranceFacade,
    private marineInsuranceState: MarineInsuranceState,
    private entrySummaryStateService: EntrySummaryStateService,
    private companiesService: CompaniesService,
    public alertService: AlertService,
  ) {}

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onOpenQuoteSidebar(
    entityType: enumMarineInsuranceEntityType,
    entityData: { isf?: IsfModel; entrySummary?: EntrySummaryModel; lclQuote?: QuoteModel; lclShipment?: LCLShipmentModel },
    marineInsurance: MarineInsuranceModel,
    isEdit: boolean = false,
    isAdmin: boolean = false,
  ): void {
    const componentSidebarRef: ComponentRef<MarineInsuranceSidebarQuoteFormComponent> = this.appService.openSidebar(
      MarineInsuranceSidebarQuoteFormComponent,
      {
        sidebarHeaderText: isEdit ? 'MARINE_INSURANCE_EDIT_TITLE' : 'MARINE_INSURANCE_TITLE',
        isPurple: true,
      },
    );

    componentSidebarRef.instance.entity = entityType;
    componentSidebarRef.instance.marineInsurance = marineInsurance;
    componentSidebarRef.instance.isf = entityData.isf;
    componentSidebarRef.instance.entrySummary = entityData.entrySummary;
    componentSidebarRef.instance.lclQuote = entityData.lclQuote;
    componentSidebarRef.instance.lclShipment = entityData.lclShipment;
    componentSidebarRef.instance.isEdit = isEdit;
    componentSidebarRef.instance.commodities$ = this.getCommodities();
    componentSidebarRef.instance.save
      .pipe(
        switchMap(
          (formData: { data: MarineInsuranceQuoteFormModel; entity: IsfModel | EntrySummaryModel | QuoteModel | LCLShipmentModel }) => {
            componentSidebarRef.instance.isSaving = true;

            return isEdit
              ? this.updateMarineInsurance(formData.data, this.marineInsuranceState.currentInsurance.getValue())
              : this.createMarineInsurance(formData, entityType, entityData.entrySummary);
          },
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(
        ([data, bills]: [MarineInsuranceModel, PaymentBillItemPendingModel]) => {
          this.marineInsuranceState.saveData(data, false, false);
          this.marineInsuranceState.saveBillsData(bills);
          this.marineInsuranceState.saveInsuranceUpdatedData();
          componentSidebarRef.instance.isSaving = false;
          this.appService.closeSidebar();
        },
        () => {
          this.marineInsuranceState.saveData(null, true, false);
          componentSidebarRef.instance.isSaving = false;
          componentSidebarRef.instance.detectChanges();
        },
      );
  }

  public onOpenQuoteManagementSidebar(entityType: enumMarineInsuranceEntityType, marineInsurance: MarineInsuranceManagementModel): void {
    const componentSidebarRef: ComponentRef<MarineInsuranceSidebarQuoteManagementFormComponent> = this.appService.openSidebar(
      MarineInsuranceSidebarQuoteManagementFormComponent,
      {
        sidebarHeaderText: 'RECALCULATE_MARINE_INSURANCE_APPLICATION',
        isPurple: true,
      },
    );

    componentSidebarRef.instance.entity = entityType;
    componentSidebarRef.instance.marineInsurance = marineInsurance;
    this.getCommodities()
      .pipe(take(1))
      .subscribe((commodities: ShortModel[]) => {
        componentSidebarRef.instance.commodities = commodities;
        componentSidebarRef.instance.detectChanges();
      });
    if (!!marineInsurance?.updatedInsuredValue?.insuredAmount) {
      componentSidebarRef.instance.isShowNewPayment = true;
      this.getPendingBills(marineInsurance, null, null, false)
        .pipe(take(1))
        .subscribe((bills: PaymentBillItemPendingModel) => {
          componentSidebarRef.instance.pendingBills = bills;
          componentSidebarRef.instance.detectChanges();
        });
    }
    componentSidebarRef.instance.recalculate
      .pipe(
        switchMap((formData: MarineInsuranceInsuredValueModel) => {
          componentSidebarRef.instance.isSaving = true;

          return this.updateMarineInsuranceManagement({ updatedInsuredValue: formData }, marineInsurance, true);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(
        ([data, bills]: [MarineInsuranceManagementModel, PaymentBillItemPendingModel]) => {
          componentSidebarRef.instance.isShowNewPayment = true;
          componentSidebarRef.instance.marineInsurance = data;
          marineInsurance.priceUpdateTimestamp = data.priceUpdateTimestamp;
          componentSidebarRef.instance.pendingBills = bills;
          componentSidebarRef.instance.isSaving = false;
          this.isModelUpdatedInSidebar$.next(true);
          componentSidebarRef.instance.detectChanges();
        },
        (error: { isProviderWasDown: boolean }) => {
          componentSidebarRef.instance.isSaving = false;
          componentSidebarRef.instance.detectChanges();

          if (!!error?.isProviderWasDown) {
            this.alertService.modalError('ANOVA_IS_DOWN_MANAGEMENT');
            componentSidebarRef.instance.discard.emit();
          }
        },
      );

    componentSidebarRef.instance.discard
      .pipe(
        switchMap(() => {
          componentSidebarRef.instance.isSaving = true;

          return this.updateMarineInsuranceManagement(
            {
              updatedInsuredValue: {
                commodityId: null,
                goodsTypeDescription: marineInsurance?.updatedInsuredValue?.goodsTypeDescription,
                insuredAmount: 0,
                typeOfShipment: null,
              },
            },
            {
              ...marineInsurance,
              updatedInsuredValue: {
                commodityId: null,
                goodsTypeDescription: marineInsurance?.updatedInsuredValue?.goodsTypeDescription,
                insuredAmount: 0,
                typeOfShipment: null,
              },
            },
          );
        }),
        switchMap(([data, bills]: [MarineInsuranceManagementModel, PaymentBillItemPendingModel]) => {
          return combineLatest([of(data), of(bills), this.resetAllFlags(marineInsurance)]);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(([data, bills, _]: [MarineInsuranceManagementModel, PaymentBillItemPendingModel, MarineInsuranceManagementModel]) => {
        componentSidebarRef.instance.isSaving = false;
        componentSidebarRef.instance.isShowNewPayment = false;
        componentSidebarRef.instance.marineInsurance = data;
        marineInsurance.priceUpdateTimestamp = data.priceUpdateTimestamp;
        componentSidebarRef.instance.pendingBills = bills;
        this.isModelUpdatedInSidebar$.next(true);
        componentSidebarRef.instance.detectChanges();
      });

    componentSidebarRef.instance.save
      .pipe(
        switchMap(() => {
          return this.marineInsuranceFacade.confirmDraftPayment(marineInsurance.id);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.isModelUpdatedInSidebar$.next(true);
        this.appService.closeSidebar();
      });
  }

  public onOpenBeneficiarySidebar(
    entityType: enumMarineInsuranceEntityType,
    entityData: { isf?: IsfModel; entrySummary?: EntrySummaryModel; lclQuote?: QuoteModel; lclShipment?: LCLShipmentModel },
    marineInsurance: MarineInsuranceModel,
  ): void {
    const componentSidebarRef: ComponentRef<MarineInsuranceSidebarBeneficiaryFormComponent> = this.appService.openSidebar(
      MarineInsuranceSidebarBeneficiaryFormComponent,
      {
        sidebarHeaderText: 'EDIT_BENEFICIARY_DETAILS',
        tooltipText: 'MARINE_INSURANCE_BENEFICIARY_HINT',
        isPurple: true,
      },
    );

    componentSidebarRef.instance.entity = entityType;
    componentSidebarRef.instance.marineInsurance = marineInsurance;
    componentSidebarRef.instance.isf = entityData.isf;
    componentSidebarRef.instance.entrySummary = entityData.entrySummary;
    componentSidebarRef.instance.lclQuote = entityData.lclQuote;
    componentSidebarRef.instance.lclShipment = entityData.lclShipment;
    componentSidebarRef.instance.save
      .pipe(
        switchMap((beneficiary: EntityModel) => {
          componentSidebarRef.instance.isSaving = true;

          return this.updateBeneficiary(this.marineInsuranceState.currentInsurance.getValue(), beneficiary);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (data: MarineInsuranceModel) => {
          this.marineInsuranceState.saveData(data, false, false);
          this.marineInsuranceState.saveInsuranceUpdatedData();
          componentSidebarRef.instance.isSaving = false;
          this.appService.closeSidebar();
        },
        () => {
          componentSidebarRef.instance.isSaving = false;
          componentSidebarRef.instance.detectChanges();
        },
      );
  }

  public onOpenFromToSidebar(
    entityType: enumMarineInsuranceEntityType,
    entityData: { isf?: IsfModel; entrySummary?: EntrySummaryModel; lclQuote?: QuoteModel; lclShipment?: LCLShipmentModel },
    marineInsurance: MarineInsuranceModel,
    isFrom: boolean,
    isEdit?: boolean,
  ): void {
    const componentSidebarRef: ComponentRef<MarineInsuranceSidebarFromToFormComponent> = this.appService.openSidebar(
      MarineInsuranceSidebarFromToFormComponent,
      {
        sidebarHeaderText: isEdit ? (isFrom ? 'EDIT_FROM' : 'EDIT_TO') : isFrom ? 'ADD_FROM' : 'ADD_TO',
        isPurple: true,
      },
    );

    componentSidebarRef.instance.entity = entityType;
    componentSidebarRef.instance.marineInsurance = marineInsurance;
    componentSidebarRef.instance.isf = entityData.isf;
    componentSidebarRef.instance.entrySummary = entityData.entrySummary;
    componentSidebarRef.instance.lclQuote = entityData.lclQuote;
    componentSidebarRef.instance.lclShipment = entityData.lclShipment;
    componentSidebarRef.instance.isFrom = isFrom;
    componentSidebarRef.instance.isEdit = isEdit;
    componentSidebarRef.instance.save
      .pipe(
        switchMap((formData: MarineInsuranceQuoteFormModel) => {
          componentSidebarRef.instance.isSaving = true;

          /* we need to get the latest value of insurance */
          return this.updateMarineInsurance(formData, this.marineInsuranceState.currentInsurance.getValue());
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(
        ([data, bills]: [MarineInsuranceModel, PaymentBillItemPendingModel]) => {
          this.marineInsuranceState.saveData(data, false, false);
          this.marineInsuranceState.saveBillsData(bills);
          this.marineInsuranceState.saveInsuranceUpdatedData();
          componentSidebarRef.instance.isSaving = false;
          this.appService.closeSidebar();
        },
        () => {
          this.marineInsuranceState.saveData(null, true, false);
          componentSidebarRef.instance.isSaving = false;
          componentSidebarRef.instance.detectChanges();
        },
      );
  }

  public onOpenDescriptionManagementSidebar(marineInsurance: MarineInsuranceManagementModel): void {
    const componentSidebarRef: ComponentRef<MarineInsuranceSidebarDescriptionManagementFormComponent> = this.appService.openSidebar(
      MarineInsuranceSidebarDescriptionManagementFormComponent,
      {
        sidebarHeaderText: 'EDIT_INSURED_VALUE_AND_COMMODITY',
        isPurple: true,
      },
    );

    componentSidebarRef.instance.marineInsurance = marineInsurance;
    componentSidebarRef.instance.save
      .pipe(
        switchMap((formData: MarineInsuranceQuoteFormModel) => {
          componentSidebarRef.instance.isSaving = true;

          return this.marineInsuranceFacade.updateQuoteManagement(formData, marineInsurance);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        componentSidebarRef.instance.isSaving = false;
        this.appService.closeSidebar();
        this.isSidebarClosing$.next(true);
      });
  }

  public onOpenBeneficiaryManagementSidebar(
    entityType: enumMarineInsuranceEntityType,
    entityData: { isf?: IsfModel; entrySummary?: EntrySummaryModel; lclQuote?: QuoteModel; lclShipment?: LCLShipmentModel },
    marineInsurance: MarineInsuranceManagementModel,
  ): void {
    const componentSidebarRef: ComponentRef<MarineInsuranceSidebarBeneficiaryFormComponent> = this.appService.openSidebar(
      MarineInsuranceSidebarBeneficiaryFormComponent,
      {
        sidebarHeaderText: 'EDIT_BENEFICIARY_DETAILS',
        isPurple: true,
      },
    );

    componentSidebarRef.instance.entity = entityType;
    componentSidebarRef.instance.marineInsurance = marineInsurance;
    componentSidebarRef.instance.isf = entityData.isf;
    componentSidebarRef.instance.entrySummary = entityData.entrySummary;
    componentSidebarRef.instance.lclQuote = entityData.lclQuote;
    componentSidebarRef.instance.lclShipment = entityData.lclShipment;
    componentSidebarRef.instance.save
      .pipe(
        switchMap((beneficiary: EntityModel) => {
          componentSidebarRef.instance.isSaving = true;

          return this.updateManagementBeneficiary(marineInsurance, beneficiary);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        componentSidebarRef.instance.isSaving = false;
        this.isSidebarClosing$.next(true);
        this.appService.closeSidebar();
      });
  }

  public onOpenShipmentDetailsManagementSidebar(marineInsurance: MarineInsuranceManagementModel): void {
    const componentSidebarRef: ComponentRef<MarineInsuranceSidebarShipmentDetailsFormComponent> = this.appService.openSidebar(
      MarineInsuranceSidebarShipmentDetailsFormComponent,
      {
        sidebarHeaderText: 'LABELS.EDIT_SHIPMENT_DETAILS',
        isPurple: true,
      },
    );

    componentSidebarRef.instance.marineInsurance = marineInsurance;
    componentSidebarRef.instance.save
      .pipe(
        switchMap((formData: MarineInsuranceQuoteManagementFormModel) => {
          componentSidebarRef.instance.isSaving = true;

          return this.updateMarineInsuranceManagement(formData, marineInsurance);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        componentSidebarRef.instance.isSaving = false;
        this.appService.closeSidebar();
        this.isSidebarClosing$.next(true);
      });
  }

  private getCommodities(): Observable<ShortModel[]> {
    return this.marineInsuranceFacade.getCommodities();
  }

  private createMarineInsurance(
    formData: { data: MarineInsuranceQuoteFormModel; entity: IsfModel | EntrySummaryModel | QuoteModel | LCLShipmentModel },
    entityType: enumMarineInsuranceEntityType,
    es?: EntrySummaryModel,
  ): Observable<[MarineInsuranceModel, PaymentBillItemPendingModel]> {
    const currentCompany: CompanyModel = this.companiesService.getCompany();

    return this.marineInsuranceFacade.createQuote(formData.data, formData.entity, entityType, currentCompany.id).pipe(
      switchMap(([data, bill]: [MarineInsuranceModel, PaymentBillItemPendingModel]) => {
        if (entityType === enumMarineInsuranceEntityType.ENTRY_SUMMARY && !es?.shipments?.[0]?.exportDate) {
          return this.updateESShipment(es, { ...es?.shipments?.[0], exportDate: formData.data.shippingDate }).pipe(
            switchMap(() => combineLatest([of(data), of(bill)])),
          );
        } else {
          return combineLatest([of(data), of(bill)]);
        }
      }),
    );
  }

  private updateMarineInsurance(
    formData: MarineInsuranceQuoteFormModel,
    marineInsurance: MarineInsuranceModel,
  ): Observable<[MarineInsuranceModel, PaymentBillItemPendingModel]> {
    const currentCompany: CompanyModel = this.companiesService.getCompany();

    const skipBillsPolling: boolean =
      formData?.insuredAmount === marineInsurance.insuredValue?.insuredAmount &&
      formData?.commodityId === marineInsurance.insuredValue?.commodity?.id;

    return this.marineInsuranceFacade.updateQuote(formData, marineInsurance, currentCompany.id, skipBillsPolling);
  }

  private updateESShipment(es: EntrySummaryModel, data: Partial<ShipmentModel>): Observable<ShipmentModel> {
    // due to circular DI I set it here

    const updatedES: EntrySummaryModel = { ...es };
    if (es?.shipments?.[0]) {
      updatedES.shipments[0].exportDate = data.exportDate;
    }

    return this.entrySummaryStateService
      .updateShipment(es?.shipments?.[0], data)
      .pipe(tap(() => this.entrySummaryStateService.updateCurrentEntrySummary(es)));
  }

  private updateBeneficiary(marineInsurance: MarineInsuranceModel, beneficiary: EntityModel): Observable<MarineInsuranceModel> {
    const currentCompany: CompanyModel = this.companiesService.getCompany();

    return this.marineInsuranceFacade.updateBeneficiary(marineInsurance, beneficiary, currentCompany?.id);
  }

  private updateManagementBeneficiary(
    marineInsurance: MarineInsuranceManagementModel,
    beneficiary: EntityModel,
  ): Observable<MarineInsuranceManagementModel> {
    return this.marineInsuranceFacade.updateManagementBeneficiary(marineInsurance, beneficiary);
  }

  private updateMarineInsuranceManagement(
    formData: MarineInsuranceQuoteManagementFormModel,
    marineInsurance: MarineInsuranceManagementModel,
    isForceDowntimeCheck?: boolean,
  ): Observable<[MarineInsuranceManagementModel, PaymentBillItemPendingModel]> {
    const skipBillsPolling: boolean =
      formData?.updatedInsuredValue?.insuredAmount === marineInsurance?.updatedInsuredValue?.insuredAmount &&
      formData?.updatedInsuredValue?.commodityId === marineInsurance?.updatedInsuredValue?.commodity?.id;

    return this.marineInsuranceFacade.updateQuoteManagement(formData, marineInsurance, skipBillsPolling, isForceDowntimeCheck);
  }

  private getPendingBills(
    marineInsurance: MarineInsuranceManagementModel,
    paymentProvider: enumPaymentProvider,
    paymentCodeId: number,
    isCalculateFee: boolean = false,
  ): Observable<PaymentBillItemPendingModel> {
    return this.marineInsuranceFacade.getPendingBills(
      marineInsurance?.companyId,
      [marineInsurance.referenceNumber],
      paymentProvider,
      paymentCodeId,
      isCalculateFee,
    );
  }

  private resetAllFlags(marineInsurance: MarineInsuranceManagementModel): Observable<MarineInsuranceManagementModel> {
    return this.marineInsuranceFacade.setManagementFlags(marineInsurance.id);
  }
}
